import { useCallback, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';

import {
  NotificationManager,
  SessionStorageManager,
  SiteConfigManager,
  StorageManager,
} from '@atlas/core';

const DEPOSIT_EXIT_POPUP_STORAGE_KEY = 'depositExitPopup';
const SUCCESSFUL_DEPOSIT_SESSION_KEY = 'hasSuccessfulDeposit';

const getLocationPath = ({ pathname = '', search = '', hash = '' } = {}) =>
  `${pathname}${search}${hash}`;

const normalizePath = (path = '') => path.replace(/\/+$/, '') || '/';

const isPathInsideAllowedPath = (path, allowedPathPrefix) => {
  if (!allowedPathPrefix) return false;

  const nextPath = normalizePath(path);
  const allowedPath = normalizePath(allowedPathPrefix);

  return nextPath === allowedPath || nextPath.startsWith(`${allowedPath}/`);
};

const useDepositExitPopup = ({ allowedPathPrefix, showPopupOnAction }) => {
  const {
    cooldown,
    enabled: depositExitPopupEnabled,
    maxShowsPerSession,
    timePeriodForMaxShows,
  } = SiteConfigManager.getConfig('config.config.general.depositExitIntentPopup', {
    cooldown: 0,
    enabled: false,
    maxShowsPerSession: 0,
    staticBlock: '',
    timePeriodForMaxShows: 0,
  });
  const timePeriodForMaxShowsInMs = timePeriodForMaxShows * 60 * 60 * 1000;
  const history = useHistory();
  const unblockRef = useRef(null);
  const pendingLocationRef = useRef(null);

  const confirmLeave = useCallback(() => {
    const pendingLocation = pendingLocationRef.current;
    if (!pendingLocation) return;

    if (unblockRef.current) {
      unblockRef.current();
      unblockRef.current = null;
    }

    history.push(pendingLocation.pathname + pendingLocation.search + pendingLocation.hash);
  }, [history]);

  const setHasSuccessfulDepositInSession = useCallback(() => {
    SessionStorageManager.setItem(SUCCESSFUL_DEPOSIT_SESSION_KEY, true);
  }, []);

  const hasSuccessfulDepositInSession = useCallback(() => {
    return SessionStorageManager.getItem(SUCCESSFUL_DEPOSIT_SESSION_KEY) === 'true';
  }, []);

  const cancelLeave = useCallback(() => {
    pendingLocationRef.current = null;
  }, []);

  const saveLastOpenData = useCallback(() => {
    const now = Date.now();
    const savedOpeningData = JSON.parse(StorageManager.getItem(DEPOSIT_EXIT_POPUP_STORAGE_KEY));
    const openedCount = savedOpeningData?.openedCount || 0;
    const firstOpened = savedOpeningData?.firstOpened || 0;
    const isTimePeriodPassed = now - firstOpened >= timePeriodForMaxShowsInMs;

    const lastOpeningData = {
      firstOpened: !isTimePeriodPassed && openedCount !== 0 ? savedOpeningData?.firstOpened : now,
      lastOpened: now,
      openedCount: isTimePeriodPassed ? 1 : openedCount + 1,
    };

    StorageManager.setItem(DEPOSIT_EXIT_POPUP_STORAGE_KEY, JSON.stringify(lastOpeningData));
  }, [timePeriodForMaxShowsInMs]);

  const canBeOpenAccordingToConfig = useCallback(() => {
    const now = Date.now();
    const savedOpeningData = JSON.parse(StorageManager.getItem(DEPOSIT_EXIT_POPUP_STORAGE_KEY));
    const lastOpened = savedOpeningData?.lastOpened || 0;
    const openedCount = savedOpeningData?.openedCount || 0;
    const firstOpened = savedOpeningData?.firstOpened || 0;

    if (now - firstOpened > timePeriodForMaxShowsInMs) {
      return true;
    }

    const isCooldownExpired = now - lastOpened >= cooldown * 1000;
    const isMaxShowsReached = openedCount >= maxShowsPerSession;
    return isCooldownExpired && !isMaxShowsReached;
  }, [cooldown, maxShowsPerSession, timePeriodForMaxShowsInMs]);

  const openDepositExitPopup = useCallback(
    (params = {}) => {
      NotificationManager.display({
        data: {
          cancelLeave: () => {
            cancelLeave();
            params?.cancelLeaveCallback?.();
          },
          confirmLeave: () => {
            confirmLeave();
            params?.confirmLeaveCallback?.();
          },
        },
        type: 'depositExitPopup',
      });

      saveLastOpenData();
    },
    [cancelLeave, confirmLeave, saveLastOpenData],
  );

  const openDepositExitPopupByAction = ({
    cancelLeaveCallback,
    confirmLeaveCallback,
    onPopupNoOpened,
  }) => {
    if (
      !depositExitPopupEnabled ||
      !canBeOpenAccordingToConfig() ||
      hasSuccessfulDepositInSession()
    ) {
      onPopupNoOpened?.();
      return;
    }

    openDepositExitPopup({ cancelLeaveCallback, confirmLeaveCallback });
  };

  useEffect(() => {
    if (!depositExitPopupEnabled || showPopupOnAction) {
      return;
    }

    unblockRef.current = history.block(nextLocation => {
      const nextPath = nextLocation.pathname;
      const isNextInsideAllowedPath = isPathInsideAllowedPath(nextPath, allowedPathPrefix);
      const isCurrentLocation = getLocationPath(nextLocation) === getLocationPath(history.location);

      if (
        isCurrentLocation ||
        isNextInsideAllowedPath ||
        hasSuccessfulDepositInSession() ||
        !canBeOpenAccordingToConfig()
      ) {
        return true;
      }

      pendingLocationRef.current = nextLocation;

      openDepositExitPopup();

      return false;
    });

    return () => {
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
    };
  }, [
    history,
    cancelLeave,
    allowedPathPrefix,
    openDepositExitPopup,
    confirmLeave,
    depositExitPopupEnabled,
    showPopupOnAction,
    canBeOpenAccordingToConfig,
    hasSuccessfulDepositInSession,
  ]);

  return {
    openDepositExitPopupByAction,
    setHasSuccessfulDepositInSession,
  };
};

export default useDepositExitPopup;
